<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import NextButton from 'dashboard/components-next/button/Button.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import { useDebtKanbanWidget } from 'dashboard/composables/useDebtKanbanWidget';

const { t } = useI18n();

const {
  conversationId,
  isFeatureEnabled,
  card,
  hasCard,
  hasLoadedCard,
  isSubmitting,
  currentFunnelStages,
  stageOrdinalLabel,
  showRail,
  currentStageIndex,
  stageOptions,
  switchFunnelOptions,
  timeInStageLabel,
  isSlaBreached,
  isSwitchingFunnel,
  switchFunnelTarget,
  moveStage,
  startSwitchFunnel,
  cancelSwitchFunnel,
  selectSwitchFunnelTarget,
  confirmSwitchFunnel,
} = useDebtKanbanWidget();

// Id derivado da conversa (não fixo): dois widgets do mesmo tipo (ex.: troca
// rápida entre conversas) nunca colidem no DOM — mesma razão que motivou a
// correção equivalente em DebtStateBlock.vue.
const stageDropdownId = computed(() => `kanban-stage-${conversationId.value}`);
const switchFunnelDropdownId = computed(
  () => `kanban-switch-funnel-${conversationId.value}`
);

// Percorrido vs. não-percorrido não pode depender só da cor (WCAG 1.4.1):
// o preenchimento (cheio para o que já passou, contorno para o que falta)
// sobrevive ao baixo contraste entre blue-7 e slate-6.
const railDotClasses = index => {
  if (index === currentStageIndex.value) return ['bg-n-blue-9', 'size-2.5'];
  if (index < currentStageIndex.value) return ['bg-n-blue-7', 'size-1.5'];
  return ['bg-transparent', 'border', 'border-n-slate-8', 'size-1.5'];
};

const timeInStageClass = computed(() =>
  isSlaBreached.value ? 'text-n-amber-12' : 'text-n-slate-11'
);
</script>

<template>
  <div
    v-if="isFeatureEnabled && hasLoadedCard"
    class="flex flex-col gap-2 mt-4 px-3 py-3 rounded-lg border border-n-weak bg-n-background"
  >
    <template v-if="hasCard">
      <div>
        <h6 class="m-0 text-xs font-semibold text-n-slate-11">
          {{ t('CONVERSATION.KANBAN_WIDGET.FUNNEL_LABEL') }}
        </h6>
        <p
          class="m-0 text-sm text-n-slate-12 truncate"
          :title="card.funnel.name"
        >
          {{ card.funnel.name }}
        </p>
      </div>

      <div v-if="stageOrdinalLabel" class="flex items-center gap-2">
        <ol
          v-if="showRail"
          aria-hidden="true"
          class="flex items-center gap-1 m-0 p-0 list-none"
        >
          <li
            v-for="(stage, index) in currentFunnelStages"
            :key="stage.id"
            class="rounded-full"
            :class="railDotClasses(index)"
          />
        </ol>
        <span class="text-xs text-n-slate-11 whitespace-nowrap">
          {{ stageOrdinalLabel }}
        </span>
      </div>

      <div>
        <p class="m-0 mb-1.5 text-sm font-medium text-n-slate-12">
          {{ t('CONVERSATION.KANBAN_WIDGET.STAGE_LABEL') }}
        </p>
        <MultiselectDropdown
          :id="stageDropdownId"
          :options="stageOptions"
          :selected-item="card.stage"
          :has-thumbnail="false"
          :multiselector-title="
            t('CONVERSATION.KANBAN_WIDGET.STAGE_PICKER_TITLE')
          "
          :multiselector-placeholder="
            t('CONVERSATION.KANBAN_WIDGET.STAGE_PICKER_PLACEHOLDER')
          "
          :no-search-result="
            t('CONVERSATION.KANBAN_WIDGET.STAGE_PICKER_NO_RESULTS')
          "
          :input-placeholder="
            t('CONVERSATION.KANBAN_WIDGET.STAGE_PICKER_SEARCH_PLACEHOLDER')
          "
          @select="moveStage"
        />
      </div>

      <p
        v-if="timeInStageLabel"
        class="m-0 flex items-center gap-1 text-xs"
        :class="timeInStageClass"
      >
        <span
          v-if="isSlaBreached"
          aria-hidden="true"
          class="i-lucide-triangle-alert size-3 shrink-0"
        />
        {{ timeInStageLabel }}
      </p>

      <NextButton
        v-if="!isSwitchingFunnel"
        link
        slate
        xs
        class="self-start underline rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-blue-9"
        :label="t('CONVERSATION.KANBAN_WIDGET.SWITCH_FUNNEL_ACTION')"
        @click="startSwitchFunnel"
      />
      <div v-else class="flex flex-col gap-2">
        <div>
          <p class="m-0 mb-1.5 text-sm font-medium text-n-slate-12">
            {{ t('CONVERSATION.KANBAN_WIDGET.SWITCH_FUNNEL_LABEL') }}
          </p>
          <MultiselectDropdown
            :id="switchFunnelDropdownId"
            :options="switchFunnelOptions"
            :selected-item="switchFunnelTarget"
            :has-thumbnail="false"
            :multiselector-title="
              t('CONVERSATION.KANBAN_WIDGET.SWITCH_FUNNEL_TITLE')
            "
            :multiselector-placeholder="
              t('CONVERSATION.KANBAN_WIDGET.SWITCH_FUNNEL_PLACEHOLDER')
            "
            :no-search-result="
              t('CONVERSATION.KANBAN_WIDGET.SWITCH_FUNNEL_NO_RESULTS')
            "
            :input-placeholder="
              t('CONVERSATION.KANBAN_WIDGET.SWITCH_FUNNEL_SEARCH_PLACEHOLDER')
            "
            @select="selectSwitchFunnelTarget"
          />
        </div>
        <div v-if="switchFunnelTarget" class="flex flex-col gap-1.5">
          <p class="m-0 text-xs text-n-slate-11">
            {{
              t('CONVERSATION.KANBAN_WIDGET.SWITCH_FUNNEL_CONFIRM_PROMPT', {
                funnel: switchFunnelTarget.name,
              })
            }}
          </p>
          <div class="flex items-center gap-1">
            <!-- !text-n-blue-12: a variante compartilhada `blue faded`
                 (bg-n-brand/10 text-n-blue-11) dá 4,05:1 no tema claro,
                 abaixo dos 4,5:1 de 1.4.3 — e aqui, como no par
                 Confirmar/Descartar do DEBTHUB, a ação afirmativa era menos
                 legível que o Cancelar ao lado. O override vale nos dois
                 temas (10,43:1 claro, 12,21:1 escuro) e não toca o token,
                 usado em 30 arquivos fora daqui. Mesmo remédio já medido em
                 KanbanPeriodFilter.vue. -->
            <NextButton
              xs
              faded
              color="blue"
              class="!text-n-blue-12"
              :is-loading="isSubmitting"
              :disabled="isSubmitting"
              :label="t('CONVERSATION.KANBAN_WIDGET.CONFIRM')"
              @click="confirmSwitchFunnel"
            />
            <NextButton
              xs
              ghost
              color="slate"
              :label="t('CONVERSATION.KANBAN_WIDGET.CANCEL')"
              @click="cancelSwitchFunnel"
            />
          </div>
        </div>
        <NextButton
          v-else
          link
          slate
          xs
          class="self-start"
          :label="t('CONVERSATION.KANBAN_WIDGET.CANCEL')"
          @click="cancelSwitchFunnel"
        />
      </div>
    </template>

    <!--
      Estado vazio SEM ação: T3.2 não expõe endpoint de criação de card
      (config/routes.rb, `resources :cards, only: [:update]`) — não há como
      vincular esta conversa a um funil a partir do frontend hoje. Ver
      RISCOS/PENDÊNCIAS do retorno de implementação.
    -->
    <template v-else>
      <h6 class="m-0 text-xs font-semibold text-n-slate-11">
        {{ t('CONVERSATION.KANBAN_WIDGET.EMPTY_TITLE') }}
      </h6>
      <p class="m-0 text-sm text-n-slate-12">
        {{ t('CONVERSATION.KANBAN_WIDGET.EMPTY_DESCRIPTION') }}
      </p>
    </template>
  </div>
</template>
